import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { mediaSrc, avatarSrc, prefetchMedia } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MediaCarousel from "./MediaCarousel";
import { Heart, MessageCircle, Share2, Eye, Play, Lock, Volume2, VolumeX, Bookmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import CommentThread from "./CommentThread";
import { VerifiedBadge } from "./VerifiedBadge";
import { MentionText } from "./MentionText";
import { TaggedPeople } from "./TaggedPeople";
import { NotesModal, PostMenu, ReblogButton } from "./PostExtras";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

export default function PostCard({ item: initial }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [item, setItem] = useState(initial);
  const [muted, setMuted] = useState(true);
  const [showComments, setShowComments] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const isVideo = item.media_type === "video";

  useEffect(() => { setItem(initial); }, [initial]);

  // autoplay muted when the video scrolls into view
  useEffect(() => {
    if (!isVideo) return;
    const el = containerRef.current;
    const v = videoRef.current;
    if (!el || !v) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && e.intersectionRatio > 0.55) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: [0.55] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isVideo]);

  const like = async () => {
    if (!user) return nav("/login");
    try {
      const { data } = await api.post(`/media/${item.id}/like`);
      setItem((m) => ({ ...m, liked: data.liked, likes_count: data.likes_count }));
    } catch { /* noop */ }
  };

  const share = () => {
    const url = `${window.location.origin}/api/og/${item.id}`;
    if (navigator.share) navigator.share({ title: item.title, url }).catch(() => {});
    else { navigator.clipboard?.writeText(url); toast.success("Link copied"); }
  };

  const toggleSave = async () => {
    if (!user) return nav("/login");
    setItem((m) => ({ ...m, saved: !m.saved }));
    try {
      const { data } = await api.post(`/media/${item.id}/save`);
      setItem((m) => ({ ...m, saved: data.saved }));
      toast.success(data.saved ? "Saved for later" : "Removed from saved");
    } catch {
      setItem((m) => ({ ...m, saved: !m.saved }));
      toast.error("Couldn't save");
    }
  };

  const changeCount = (d) => setItem((m) => ({ ...m, comments_count: Math.max(0, (m.comments_count || 0) + d) }));

  const name = item.author_username;

  return (
    <article data-testid={`post-card-${item.id}`} className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-xl overflow-hidden mb-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* header */}
      <div className="flex items-center justify-between p-3 sm:p-4">
        <Link to={`/u/${name}`} data-testid={`post-author-${item.id}`} className="flex items-center gap-3 group min-w-0">
          {item.author_avatar ? (
            <img data-testid={`post-avatar-${item.id}`} src={avatarSrc(item.author_avatar)} alt={name} className="w-11 h-11 rounded-full object-cover bg-[#141414] shrink-0" />
          ) : (
            <div data-testid={`post-avatar-${item.id}`} className="w-11 h-11 rounded-full bg-[#0066FF] flex items-center justify-center text-white font-semibold shrink-0">{(item.author_display_name || name)?.[0]?.toUpperCase()}</div>
          )}
          <div className="min-w-0">
            <p data-testid={`post-author-name-${item.id}`} className="text-[#FFFFFF] font-semibold text-[15px] leading-tight flex items-center gap-1 min-w-0"><span className="truncate group-hover:underline">{item.author_display_name || name}</span><VerifiedBadge show={item.author_verified} testId={`post-verified-${item.id}`} /></p>
            <p data-testid={`post-author-username-${item.id}`} className="text-[#8A8D91] text-[13px] leading-tight truncate">@{name}</p>
            <p className="text-[#52525B] text-xs flex items-center gap-1.5 mt-0.5">
              {timeAgo(item.created_at)}
              {item.category && <span className="text-[#4599FF]">· {item.category}</span>}
            </p>
          </div>
        </Link>
        <div className="flex items-center gap-1 shrink-0">
        {item.is_private && (
          <span data-testid={`draft-badge-${item.id}`} className="flex items-center gap-1 bg-[#F1A817] text-black px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide">
            <Lock size={10} /> Draft
          </span>
        )}
        <button
          data-testid={`post-save-${item.id}`}
          onClick={toggleSave}
          aria-label={item.saved ? "Remove from saved" : "Save for later"}
          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-[#141414] ${item.saved ? "text-[#00E5FF]" : "text-[#A1A1AA]"}`}
        >
          <Bookmark size={18} fill={item.saved ? "#00E5FF" : "none"} />
        </button>
        <PostMenu item={item} />
        </div>
      </div>

      {/* title / caption */}
      {(item.title || item.ai_caption || item.description || item.tagged_users?.length > 0) && (
        <div className="px-3 sm:px-4 pb-3">
          {item.title && <h2 className="text-[#FFFFFF] font-semibold text-lg leading-snug">{item.title}</h2>}
          {(item.description || item.ai_caption) && (
            <p data-testid={`post-description-${item.id}`} className="text-[#A1A1AA] text-sm mt-1 leading-relaxed">{item.description ? <MentionText text={item.description} /> : item.ai_caption}</p>
          )}
          {item.tagged_users?.length > 0 && <div className="mt-1.5"><TaggedPeople people={item.tagged_users} testid={`post-tagged-${item.id}`} /></div>}
        </div>
      )}

      {/* media */}
      {item.is_set && item.assets?.length > 1 ? (
        <div className="relative bg-black" data-testid={`post-set-${item.id}`}>
          <MediaCarousel assets={item.assets} compact testid={`post-carousel-${item.id}`} heightClass="media-fit" onImageClick={() => nav(`/media/${item.id}`)} />
        </div>
      ) : (
      <Link to={`/media/${item.id}`} ref={containerRef} onMouseEnter={() => prefetchMedia(item.id)} onTouchStart={() => prefetchMedia(item.id)} className="block relative bg-black media-fit overflow-hidden" data-testid={`post-media-${item.id}`}>
        {isVideo && item.processing && (
          <span data-testid={`post-processing-${item.id}`} className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5 bg-black/75 backdrop-blur-sm text-[#00E5FF] px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide">
            <Loader2 size={11} className="animate-spin" /> Compressing…
          </span>
        )}
        {isVideo ? (
          <>
            <video
              ref={videoRef}
              src={mediaSrc(item.url)}
              poster={item.thumb_url ? mediaSrc(item.thumb_url) : undefined}
              muted={muted}
              loop
              playsInline
              preload="metadata"
              className="w-full media-fit object-contain bg-black"
              onClick={(e) => { e.preventDefault(); nav(`/media/${item.id}`); }}
            />
            <button
              data-testid={`post-mute-${item.id}`}
              onClick={(e) => { e.preventDefault(); setMuted((m) => !m); }}
              className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
            </button>
            <span className="absolute top-3 left-3 bg-black/60 text-white p-1.5 rounded-md pointer-events-none"><Play size={13} fill="white" /></span>
          </>
        ) : (
          <img src={mediaSrc(item.url)} alt={item.ai_caption || item.title} loading="lazy" className="w-full media-fit object-contain bg-black" />
        )}
      </Link>
      )}

      {/* tags */}
      {item.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 sm:px-4 pt-3">
          {item.tags.slice(0, 6).map((t) => (
            <Link key={t} to={`/explore?tag=${t}`} className="px-2.5 py-1 rounded-full bg-[#131C2E] border border-[#38BDF8]/20 text-[#38BDF8] text-xs font-medium hover:bg-[#1B273E] transition-colors">#{t}</Link>
          ))}
        </div>
      )}

      {/* counts */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1 text-[#52525B] text-[13px]">
        <button data-testid={`post-notes-${item.id}`} onClick={() => setShowNotes(true)} className="flex items-center gap-1 hover:text-white transition-colors">
          {(item.likes_count || 0) + (item.reblogs_count || 0)} notes
        </button>
        <span className="flex items-center gap-3">
          {item.comments_count > 0 && <span>{item.comments_count} comments</span>}
          <span className="flex items-center gap-1"><Eye size={13} /> {item.views}</span>
        </span>
      </div>

      {/* action bar */}
      <div className="flex items-center justify-between px-2 py-1 mx-2 border-t border-[#1C1C1F]">
        <button data-testid={`post-like-${item.id}`} onClick={like} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md hover:bg-[#141414] transition-colors font-semibold text-sm ${item.liked ? "text-[#E60023]" : "text-[#A1A1AA]"}`}>
          <Heart size={19} fill={item.liked ? "#E60023" : "none"} /> Like
        </button>
        <button data-testid={`post-comment-${item.id}`} onClick={() => setShowComments((s) => !s)} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md hover:bg-[#141414] transition-colors font-semibold text-sm ${showComments ? "text-[#4599FF]" : "text-[#A1A1AA]"}`}>
          <MessageCircle size={19} /> Comment
        </button>
        <ReblogButton item={item} onChange={(c) => setItem((m) => ({ ...m, ...c }))}
          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md hover:bg-[#141414] transition-colors font-semibold text-sm" />
        <button data-testid={`post-share-${item.id}`} onClick={share} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md hover:bg-[#141414] transition-colors font-semibold text-sm text-[#A1A1AA]">
          <Share2 size={19} /> Share
        </button>
      </div>

      {showComments && (
        <div className="border-t border-[#1C1C1F] px-3 sm:px-4 py-3" data-testid={`comments-section-${item.id}`}>
          <CommentThread mediaId={item.id} onCountChange={changeCount} />
        </div>
      )}
      <NotesModal mediaId={item.id} open={showNotes} onClose={() => setShowNotes(false)} />
    </article>
  );
}
